import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Mail, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Client login — whitelabel login for sub-account client users.
 * URL: /client-login/:subAccountId   (or /l/:subAccountId)
 *
 * Branding is fetched from GET /api/sub-accounts/:id/branding (public).
 * Host-based branding (custom domain) is fetched from /api/branding/by-host.
 */
export default function ClientLoginPage() {
  const { subAccountId } = useParams();
  const navigate = useNavigate();
  const [branding, setBranding] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Forced password reset (first login w/ temp password)
  const [mustResetPwd, setMustResetPwd] = useState(false);
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pendingLogin, setPendingLogin] = useState(null); // {token, workspace_id, sub_account_id, email}
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    (async () => {
      let resolved = null;
      // 1) Try host-based (custom domain) branding first
      try {
        const host = window.location.hostname;
        const hostRes = await axios.get(`${API}/api/branding/by-host`, { params: { host } });
        if (hostRes.data?.matched) resolved = hostRes.data;
      } catch { /* ignore */ }
      // 2) Fall back to sub-account branding
      if (!resolved && subAccountId) {
        try {
          const r = await axios.get(`${API}/api/sub-accounts/${subAccountId}/branding`);
          resolved = r.data;
        } catch { /* ignore */ }
      }
      setBranding(resolved || { business_name: 'Client Portal', theme_color: '#10b981' });
    })();
  }, [subAccountId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!subAccountId) {
      toast.error('Missing sub-account — ask your administrator for your portal link');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/client/login`, {
        email: email.trim().toLowerCase(), password, sub_account_id: subAccountId,
      });
      const { token, workspace_id, needs_password_reset, email: rEmail } = res.data;

      if (needs_password_reset) {
        // Hold the login state — user must pick a new password first.
        setPendingLogin({ token, workspace_id, sub_account_id: subAccountId, email: rEmail });
        setMustResetPwd(true);
        return;
      }

      localStorage.setItem('token', token);
      localStorage.setItem('voxinity_token', token);
      localStorage.setItem('auth_token', token);
      toast.success('Welcome in');
      navigate(`/workspace/${workspace_id}/sub-account/${subAccountId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally { setSubmitting(false); }
  };

  const completePasswordReset = async (e) => {
    e.preventDefault();
    if (newPwd.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (newPwd !== confirmPwd) { toast.error('Passwords do not match'); return; }
    if (newPwd === password) { toast.error('New password must differ from the temporary one'); return; }
    setResetting(true);
    try {
      await axios.post(`${API}/api/auth/reset-temporary-password`, {
        email: pendingLogin.email,
        current_password: password,
        new_password: newPwd,
      });
      // After reset, persist the JWT we held and enter the app.
      localStorage.setItem('token', pendingLogin.token);
      localStorage.setItem('voxinity_token', pendingLogin.token);
      localStorage.setItem('auth_token', pendingLogin.token);
      toast.success('Password updated — welcome in');
      navigate(`/workspace/${pendingLogin.workspace_id}/sub-account/${pendingLogin.sub_account_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update password');
    } finally { setResetting(false); }
  };

  const accent = branding?.theme_color || '#10b981';

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#08090d] px-4" data-testid="client-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          {branding?.logo_url ? (
            <img src={branding.logo_url} alt={branding.business_name} className="mx-auto mb-4 max-h-16 object-contain" />
          ) : (
            <div
              className="mx-auto mb-4 w-14 h-14 rounded-2xl flex items-center justify-center text-2xl font-bold text-white"
              style={{ background: `linear-gradient(135deg, ${accent}, ${accent}90)` }}
            >
              {(branding?.business_name || 'C').charAt(0)}
            </div>
          )}
          <h1 className="text-2xl font-semibold text-white">{branding?.business_name || 'Client Portal'}</h1>
          <p className="text-sm text-gray-400 mt-1">{branding?.tagline || 'Sign in to your account'}</p>
        </div>

        {mustResetPwd ? (
          <form
            onSubmit={completePasswordReset}
            className="bg-[#0c0d12] border border-white/[0.06] rounded-2xl p-6 space-y-4"
            data-testid="client-force-reset-form"
          >
            <div className="text-center pb-1">
              <h2 className="text-sm font-semibold text-white">Set your new password</h2>
              <p className="text-[11px] text-gray-500 mt-1">First sign-in — choose a password you'll remember.</p>
            </div>
            <div>
              <label className="text-xs text-gray-400 font-medium mb-1.5 block">New password</label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  required type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)}
                  minLength={8}
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.08] text-white placeholder-gray-500 text-sm focus:outline-none focus:border-teal-400/50"
                  placeholder="Min. 8 characters"
                  data-testid="client-force-reset-new"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-400 font-medium mb-1.5 block">Confirm new password</label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  required type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)}
                  minLength={8}
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.08] text-white placeholder-gray-500 text-sm focus:outline-none focus:border-teal-400/50"
                  data-testid="client-force-reset-confirm"
                />
              </div>
            </div>
            <button
              type="submit" disabled={resetting}
              className="w-full py-2.5 rounded-lg text-white font-semibold text-sm disabled:opacity-60 inline-flex items-center justify-center gap-2"
              style={{ background: accent }}
              data-testid="client-force-reset-submit"
            >
              {resetting ? <Loader2 size={14} className="animate-spin" /> : null}
              {resetting ? 'Saving…' : 'Set password & continue'}
            </button>
          </form>
        ) : (
          <form
            onSubmit={submit}
            className="bg-[#0c0d12] border border-white/[0.06] rounded-2xl p-6 space-y-4"
            data-testid="client-login-form"
          >
            <div>
              <label className="text-xs text-gray-400 font-medium mb-1.5 block">Email</label>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.08] text-white placeholder-gray-500 text-sm focus:outline-none focus:border-teal-400/50"
                  placeholder="you@company.com"
                  data-testid="client-login-email"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-400 font-medium mb-1.5 block">Password</label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  required type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.08] text-white placeholder-gray-500 text-sm focus:outline-none focus:border-teal-400/50"
                  data-testid="client-login-password"
                />
              </div>
            </div>
            <button
              type="submit" disabled={submitting}
              className="w-full py-2.5 rounded-lg text-white font-semibold text-sm disabled:opacity-60 inline-flex items-center justify-center gap-2"
              style={{ background: accent }}
              data-testid="client-login-submit"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        )}

        <p className="text-[11px] text-center text-gray-600 mt-6">
          Need help? Contact your account manager at {branding?.business_name || 'your provider'}.
        </p>
      </div>
    </div>
  );
}
